import React, { useEffect, useMemo, useState } from 'react';
import { X, Download, Copy, Share2, User, Coins } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';

const CustomerQRCode = ({ onClose, customerData }) => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  
  // Compose payload with explicit type, name and email for scanning
  const payload = useMemo(() => ({
    type: 'customer',
    name: currentUser?.displayName || 'Customer',
    email: currentUser?.email || '',
    customerId: currentUser?.uid || ''
  }), [currentUser]);

  // For best compatibility with generic QR scanner apps, encode a simple plain text
  // string rather than JSON so scanners show name/email directly.
  const qrText = useMemo(() => `Name: ${payload.name}\nEmail: ${payload.email}`, [payload]);

  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const url = await QRCode.toDataURL(qrText, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 256,
        });
        if (mounted) setQrCodeUrl(url);
      } catch (e) {
        console.error('QR generate failed', e);
        toast.error('Failed to generate QR');
      }
    })();
    return () => { mounted = false; };
  }, [qrText]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.download = `${payload.name || 'Customer'}-QR.png`;
    link.href = qrCodeUrl;
    link.click();
    toast.success('QR Code downloaded!');
  };

  const handleCopyData = () => {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success('Customer data copied to clipboard!');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${payload.name} - Customer QR Code`,
          text: `My customer QR code for rewards and transactions`,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      handleCopyData();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">My QR Code</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* QR Code Display */}
          <div className="text-center mb-6">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 inline-block mb-4">
              {qrCodeUrl ? (
                <img
                  src={qrCodeUrl}
                  alt="Customer QR Code"
                  className="w-48 h-48 mx-auto"
                />
              ) : (
                <div className="w-48 h-48 mx-auto flex items-center justify-center text-gray-400">
                  <User className="w-10 h-10" />
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Show this QR code to businesses for quick transactions and point collection
            </p>
          </div>

          {/* Customer Information */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <User className="w-4 h-4 mr-2" />
              Customer Information
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{payload.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium text-xs">{payload.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Points:</span>
                <span className="font-medium flex items-center">
                  <Coins className="w-3 h-3 mr-1 text-yellow-500" />
                  {customerData?.totalPoints || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Customer ID:</span>
                <span className="font-mono text-xs">{payload.customerId}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={handleDownload}
              className="flex flex-col items-center space-y-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-5 h-5 text-gray-600" />
              <span className="text-xs text-gray-600">Download</span>
            </button>
            
            <button
              onClick={handleCopyData}
              className="flex flex-col items-center space-y-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Copy className="w-5 h-5 text-gray-600" />
              <span className="text-xs text-gray-600">Copy Data</span>
            </button>
            
            <button
              onClick={handleShare}
              className="flex flex-col items-center space-y-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Share2 className="w-5 h-5 text-gray-600" />
              <span className="text-xs text-gray-600">Share</span>
            </button>
          </div>

          {/* Usage Instructions */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h5 className="font-medium text-blue-900 mb-2">How to use:</h5>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Show this QR code to businesses when making purchases</li>
              <li>• Earn points automatically when scanned</li>
              <li>• Use for quick identification and transactions</li>
              <li>• Keep it handy for reward collection</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerQRCode;
